import { queryKeys } from './useQueryUserTemplate'
import { useFetchUserTemplate } from './useFetchUserTemplate'
import { useQueryClientUtils } from '../../useQueryClientUtils'
import { useApiV1QueryClientUtils } from '../../useApiV1QueryClientUtils'
import { queryKeys as queryKeysFavourite } from '../favourite/useQueryFavourite'
import { queryKeys as queryKeysRecent } from '../recent-template/useQueryRecentTemplate'

const getQueryKeys = ({
  id,
  workspaceId,
}: {
  id: string
  workspaceId: string
}) => {
  const { language } = useLanguage()
  const queryKeyById = queryKeys.byId({ id, fieldsToInclude: ['template'] }).queryKey
  const queryKeyAll = queryKeys.all({ workspaceId }).queryKey
  const queryKeyFavourites = queryKeysFavourite.all({ workspaceId }).queryKey
  const queryKeyRecent = queryKeysRecent.all({
    locale: language,
    workspaceId,
  }).queryKey

  return {
    byId: queryKeyById,
    all: queryKeyAll,
    favourites: queryKeyFavourites,
    recent: queryKeyRecent,
  }
}

const create = () => {
  const { cancelQueries, invalidateQueries } = useQueryClientUtils()
  const { create } = useFetchUserTemplate()

  return useMutation({
    mutationFn: create,
    onMutate: ({ workspaceId }) => cancelQueries([queryKeys.all({ workspaceId }).queryKey]),
    onSettled: (_, _1, { workspaceId }) => invalidateQueries([queryKeys.all({ workspaceId }).queryKey]),
  })
}

const update = () => {
  const { cancelQueries, invalidateQueries } = useQueryClientUtils()
  const { replaceDataInQueries } = useApiV1QueryClientUtils()
  const { update } = useFetchUserTemplate()

  return useMutation({
    mutationFn: update,
    onMutate: ({ id, workspaceId }) => cancelQueries(Object.values(getQueryKeys({ id, workspaceId }))),
    onSuccess: (responseData, { workspaceId }) => {
      replaceDataInQueries(responseData.data)
      invalidateQueries([queryKeysFavourite.all({ workspaceId }).queryKey])
    },
    onError: (_, { workspaceId, id }) => invalidateQueries(Object.values(getQueryKeys({ workspaceId, id }))),
  })
}

const drop = () => {
  const queryClient = useQueryClient()
  const { cancelQueries } = useQueryClientUtils()
  const { dropDataFromQueries } = useApiV1QueryClientUtils()
  const { drop } = useFetchUserTemplate()

  return useMutation({
    mutationFn: drop,
    onMutate: ({ workspaceId, id }) => cancelQueries(Object.values(getQueryKeys({ workspaceId, id }))),
    onSettled: (_, _1, { id }) => {
      dropDataFromQueries(unref(id), 'userTemplate')
      queryClient.removeQueries({ ...queryKeys.byId({ id }) })
    },
  })
}

export const useMutationUserTemplate = {
  update,
  drop,
  create,
}
